import { useContent } from "@/context/ContentContext";
import { PAGES, AUDIENCES, ADMIN_SECTIONS, SECTION_AUDIENCES } from "@/lib/pages";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock, Globe, LayoutList } from "lucide-react";

export default function PageAccessPanel() {
  const { content, save } = useContent();
  return (
    <div data-testid="page-access-panel">
      <p className="text-sm text-zinc-500 mb-4">
        Control who can see each page. Members who don't have access won't see the page in their navigation and will be redirected if they try to open it. You (admin) always see every page.
      </p>
      <div className="space-y-2">
        {PAGES.map((p) => {
          const current = content[`access.${p.key}`] || "everyone";
          const restricted = current !== "everyone";
          return (
            <div key={p.key} className="flex items-center gap-3 border border-pb-border bg-pb-elevated/40 px-4 py-3" data-testid={`page-access-row-${p.key}`}>
              {restricted ? <Lock className="w-4 h-4 text-pb-red shrink-0" /> : <Globe className="w-4 h-4 text-zinc-600 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{p.label}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-600">{p.path}</div>
              </div>
              <Select value={current} onValueChange={(v) => save(`access.${p.key}`, v === "everyone" ? null : v)}>
                <SelectTrigger className="w-56 bg-pb-elevated border-pb-border text-sm" data-testid={`page-access-select-${p.key}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-pb-surface border-pb-border text-white">
                  {AUDIENCES.map((a) => (
                    <SelectItem key={a.value} value={a.value} data-testid={`audience-${p.key}-${a.value}`}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>

      <div className="mt-8">
        <div className="flex items-center gap-2 mb-2">
          <LayoutList className="w-4 h-4 text-pb-cyan" />
          <span className="font-display font-bold uppercase text-base">Admin Console Sections</span>
        </div>
        <p className="text-sm text-zinc-500 mb-4">
          Grant coaches and Tournament Directors access to specific admin sections (e.g. Session Availability for coaches, Tournament Hub for TDs). Granted staff get an Admin tab showing only their sections. Note: financial data sections stay admin-only on the server even if made visible.
        </p>
        <div className="space-y-2">
          {ADMIN_SECTIONS.map((s) => {
            const current = content[`adminsection.${s.key}`] || (s.key === "section-tournaments" ? "tds" : "admin");
            const restricted = current !== "admin";
            return (
              <div key={s.key} className="flex items-center gap-3 border border-pb-border bg-pb-elevated/40 px-4 py-3" data-testid={`section-access-row-${s.key}`}>
                {restricted ? <Globe className="w-4 h-4 text-pb-cyan shrink-0" /> : <Lock className="w-4 h-4 text-zinc-600 shrink-0" />}
                <div className="flex-1 min-w-0 text-sm font-semibold">{s.label}</div>
                <Select value={current} onValueChange={(v) => save(`adminsection.${s.key}`, v === "admin" && s.key !== "section-tournaments" ? null : v)}>
                  <SelectTrigger className="w-56 bg-pb-elevated border-pb-border text-sm" data-testid={`section-access-select-${s.key}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-pb-surface border-pb-border text-white">
                    {SECTION_AUDIENCES.map((a) => (
                      <SelectItem key={a.value} value={a.value} data-testid={`section-audience-${s.key}-${a.value}`}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
